import { formatDateTime } from "../content_panel/utils/helpers";
import {
  ReturnableDestinationType,
  StockTransferSubtype,
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "./model/warehouse_outward_model";
import { DisputedItemModel } from "./model/quarantine_model";

export const formatAddress = (obj?: any): string => {
  const addr = obj?.address ?? obj; // supports both shapes
  if (!addr) return "-";

  return [
    addr.line1,
    addr.line2,
    addr.line3,
    addr.city,
    addr.state,
    addr.country,
    addr.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

export const getOutwardDestinationLabel = (
  outward: WarehouseOutwardModel,
): string => {
  if (outward.type === "SO") {
    return outward.customer_name || "-";
  }

  // ST handling
  if (outward.type === "ST") {
    switch (outward.st_subtype) {
      case StockTransferSubtype.REGULAR:
        return outward.to_warehouse_name || "-";

      case StockTransferSubtype.RETURNABLE:
        return outward.destination_type === ReturnableDestinationType.VENDOR
          ? outward.vendor_name || "-"
          : outward.customer_name || "-";

      case StockTransferSubtype.FOC:
        return outward.customer_name || "-";

      case StockTransferSubtype.REPLACEMENT:
        return outward.customer_name || "-";

      case StockTransferSubtype.REVERSE_DC:
        return outward.to_warehouse_name || "-";

      default:
        return "-";
    }
  }

  return "-";
};

// Source ("From") of an outward. For a Reverse DC there is no source warehouse —
// goods are picked up from the customer/counterparty, so the pickup identity and
// address are the source.
export const getOutwardSourceLabel = (
  outward: WarehouseOutwardModel,
): string => {
  if (
    outward.type === "ST" &&
    outward.st_subtype === StockTransferSubtype.REVERSE_DC
  ) {
    return outward.customer_name || "-";
  }
  return outward.from_warehouse_name || "-";
};

// Kazam-side warehouse whose GST goes on the DC. For a Reverse DC that is the
// receiving (TO) warehouse — there is no from_warehouse.
export const getOutwardKazamWarehouseId = (
  outward: WarehouseOutwardModel,
): string | undefined => {
  if (
    outward.type === "ST" &&
    outward.st_subtype === StockTransferSubtype.REVERSE_DC
  ) {
    return outward.to_warehouse_id;
  }
  return outward.from_warehouse_id;
};

export const getOutwardSourceAddress = (outward: WarehouseOutwardModel) => {
  if (
    outward.type === "ST" &&
    outward.st_subtype === StockTransferSubtype.REVERSE_DC
  ) {
    return outward.shipping_address;
  }
  return null;
};

export const getOutwardDestinationID = (
  outward: WarehouseOutwardModel,
): string => {
  if (outward.type === "SO") {
    return outward.order_id || "-";
  }

  // ST handling
  if (outward.type === "ST") {
    switch (outward.st_subtype) {
      case StockTransferSubtype.REGULAR:
        return outward.to_warehouse_id || "-";

      case StockTransferSubtype.RETURNABLE:
        return outward.destination_type === ReturnableDestinationType.VENDOR
          ? outward.vendor_id || "-"
          : outward.customer_id || "-";

      case StockTransferSubtype.FOC:
        return outward.customer_id || "-";

      case StockTransferSubtype.REPLACEMENT:
        return outward.customer_id || "-";

      case StockTransferSubtype.REVERSE_DC:
        return outward.to_warehouse_id || "-";

      default:
        return "-";
    }
  }

  return "-";
};

export const getOutwardDestinationAddress = (
  outward: WarehouseOutwardModel,
) => {
  if (outward.type === "SO") {
    return outward.destination_address;
  }

  if (outward.type === "ST") {
    if (
      outward.st_subtype === StockTransferSubtype.RETURNABLE ||
      outward.st_subtype === StockTransferSubtype.FOC ||
      outward.st_subtype === StockTransferSubtype.REPLACEMENT ||
      outward.st_subtype === StockTransferSubtype.REVERSE_DC
    ) {
      return outward.shipping_address;
    }
  }

  return null;
};

export const getOutwardExpectedDate = (
  outward: WarehouseOutwardModel,
): string => {
  if (outward.type === "ST") {
    switch (outward.st_subtype) {
      case StockTransferSubtype.REGULAR:
        return outward.expected_date
          ? formatDateTime(outward.expected_date)
          : "-";

      case StockTransferSubtype.RETURNABLE:
        return formatDateTime(outward.expected_return_date);

      case StockTransferSubtype.FOC:
        return outward.expected_dispatch_date
          ? formatDateTime(outward.expected_dispatch_date)
          : "-";

      case StockTransferSubtype.REPLACEMENT:
        return formatDateTime(outward.expected_date);

      case StockTransferSubtype.REVERSE_DC:
        return outward.expected_date
          ? formatDateTime(outward.expected_date)
          : "-";

      default:
        return "-";
    }
  }

  return "-";
};

export const getStatusStyle = (
  status?: WarehouseOutwardStatus,
): React.CSSProperties => {
  const statusStyles: Record<string, React.CSSProperties> = {
    [WarehouseOutwardStatus.WH_YET_TO_BE_ASSIGNED]: {
      borderColor: "#FCD34D",
      backgroundColor: "#FEF3C7",
      color: "#92400E",
    },
    [WarehouseOutwardStatus.WH_ASSIGNED]: {
      borderColor: "#A5B4FC",
      backgroundColor: "#E0E7FF",
      color: "#3730A3",
    },
    [WarehouseOutwardStatus.ST_FG_APPROVAL_PENDING]: {
      borderColor: "#BEF264",
      backgroundColor: "#ECFCCB",
      color: "#4D7C0F",
    },
    [WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING]: {
      borderColor: "#C4B5FD",
      backgroundColor: "#EDE9FE",
      color: "#6D28D9",
    },
    [WarehouseOutwardStatus.MATERIAL_PICKING_PENDING]: {
      borderColor: "#67E8F9",
      backgroundColor: "#CFFAFE",
      color: "#155E75",
    },
    [WarehouseOutwardStatus.DOC_PENDING]: {
      borderColor: "#FDA4AF",
      backgroundColor: "#FFE4E6",
      color: "#BE123C",
    },
    [WarehouseOutwardStatus.READY_TO_DISPATCH]: {
      borderColor: "#93C5FD",
      backgroundColor: "#DBEAFE",
      color: "#1D4ED8",
    },
    [WarehouseOutwardStatus.TRACKING_ID_PENDING]: {
      borderColor: "#FDBA74",
      backgroundColor: "#FFEDD5",
      color: "#C2410C",
    },
    [WarehouseOutwardStatus.DISPATCHED]: {
      borderColor: "#5EEAD4",
      backgroundColor: "#CCFBF1",
      color: "#0F766E",
    },
    [WarehouseOutwardStatus.DELIVERED]: {
      borderColor: "#86EFAC",
      backgroundColor: "#DCFCE7",
      color: "#15803D",
    },
    [WarehouseOutwardStatus.REJECTED]: {
      borderColor: "#FCA5A5",
      backgroundColor: "#FEE2E2",
      color: "#B91C1C",
    },
    [WarehouseOutwardStatus.CANCELLED]: {
      borderColor: "#D1D5DB",
      backgroundColor: "#F3F4F6",
      color: "#374151",
    },
    [WarehouseOutwardStatus.RETURNED]: {
      borderColor: "#FDBA8A",
      backgroundColor: "#FFF1E6",
      color: "#9A3412",
    },
  };

  return (
    (status && statusStyles[status]) || {
      borderColor: "#D1D5DB",
      backgroundColor: "#F3F4F6",
      color: "#374151",
    }
  );
};

// Bulk paste / CSV device inward is only allowed on this ST route (mirrors backend).
export const BULK_ST_INWARD_ROUTE = { from: "WRH-938723", to: "WRH-885247" };

export const isBulkSTInwardAllowed = (
  order?: {
    type?: string;
    from?: { id?: string };
    toWarehouse?: { id?: string };
  } | null,
): boolean =>
  order?.type === "ST" &&
  order.from?.id === BULK_ST_INWARD_ROUTE.from &&
  order.toWarehouse?.id === BULK_ST_INWARD_ROUTE.to;

export const calculateIsBlockedByDispute = (
  pastDisputes?: DisputedItemModel[],
  isCurrentlyDisputed?: boolean,
): boolean => {
  if (!pastDisputes || pastDisputes.length === 0) return false;

  return (
    isCurrentlyDisputed ||
    pastDisputes.some((dispute) => dispute.status === "open")
  );
};

export const getInwardStatusClass = (status?: string | null): string => {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "received") return "status-received";
  if (s === "partial") return "status-partial";
  return "status-pending";
};

// Goods value (pre-tax) + GST derived from each item's tax_detail (percentages).
// Mirrors the backend "Total Value" (Σ itemPrice × requestedQty) and adds GST on top.
export const computeInwardTaxTotals = (
  items?: {
    itemPrice?: number;
    requestedQty?: number;
    gst_applicable?: boolean;
    tax_detail?: { cgst?: number; sgst?: number; igst?: number };
  }[],
) => {
  let subtotal = 0;
  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  (items || []).forEach((item) => {
    const itemSubtotal = (item.itemPrice || 0) * (item.requestedQty || 0);
    subtotal += itemSubtotal;

    if (item.gst_applicable && item.tax_detail) {
      cgst += (itemSubtotal * (item.tax_detail.cgst || 0)) / 100;
      sgst += (itemSubtotal * (item.tax_detail.sgst || 0)) / 100;
      igst += (itemSubtotal * (item.tax_detail.igst || 0)) / 100;
    }
  });

  return {
    subtotal,
    cgst,
    sgst,
    igst,
    gst: cgst + sgst + igst,
    total: subtotal + cgst + sgst + igst,
  };
};
